import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_META = {
  1: { label: 'Yeni Talep', className: 'new' },
  2: { label: 'Atand\u0131', className: 'assigned' },
  3: { label: '\u0130\u015flem Bekleniyor', className: 'progress' },
  4: { label: 'Tamamland\u0131', className: 'success' },
  5: { label: 'B\u00fct\u00e7e Onay\u0131nda', className: 'warning' },
  6: { label: 'M\u00fcd\u00fcr Onaylad\u0131', className: 'success' },
  7: { label: 'Muhasebeye Aktar\u0131ld\u0131', className: 'accounting' },
  8: { label: '\u0130ptal', className: 'cancelled' }
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const getRoleFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return '';

  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    return decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '';
  } catch (error) {
    return '';
  }
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (Array.isArray(responseData?.messages) && responseData.messages.length > 0) {
    return responseData.messages.join(' ');
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const getStatusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META[1];

const createAssignmentDraft = () => ({ localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, userId: '' });

const getCategoryLabel = (request) => {
  const rawValue = request.categoryType;

  if (
    rawValue === 0 ||
    rawValue === '0' ||
    rawValue === 'InternalCrane' ||
    rawValue === 'internalCrane'
  ) {
    return 'D\u0131\u015f Teknik Servis';
  }

  if (
    rawValue === 1 ||
    rawValue === '1' ||
    rawValue === 'ExternalCrane' ||
    rawValue === 'externalCrane'
  ) {
    return 'D\u0131\u015f Teknik Servis';
  }

  return 'Belirtilmedi';
};

const getMachineLabel = (request) =>
  request.machineLabel ||
  request.towerCraneSerialNo ||
  request.externalMachineName ||
  request.externalMachineModel ||
  request.externalSerialNo ||
  'Belirtilmedi';

const getWorkSiteLabel = (request) =>
  request.workSiteLabel ||
  request.workSiteName ||
  request.externalWorkSiteName ||
  'Belirtilmedi';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul'
      });
};

const TechnicalServiceRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [personnelUsers, setPersonnelUsers] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState({});
  const [assignmentNotes, setAssignmentNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [assigningId, setAssigningId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const userRole = getRoleFromToken();
  const canViewRequests = userRole === 'TechnicalServiceManager' || userRole === 'Owner';

  const fetchRequests = async () => {
    const response = await api.get('/TechnicalService/requests', { params: { status: 1 } });
    const items = response?.data?.data || [];
    setRequests(items);
    return items;
  };

  const fetchPersonnel = async () => {
    const response = await api.get('/TechnicalService/personnel');
    setPersonnelUsers(response?.data?.data || []);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!canViewRequests) return;

      try {
        setLoading(true);
        setErrorMessage('');
        const [requestItems] = await Promise.all([fetchRequests(), fetchPersonnel()]);
        const assignable = requestItems.filter((item) => normalizeStatus(item.status) === 1);
        if (assignable.length > 0) {
          const firstRequestId = assignable[0].id;
          setSelectedRequestId(firstRequestId);
          setSelectedAssignments((prev) => ({
            ...prev,
            [firstRequestId]: prev[firstRequestId]?.length ? prev[firstRequestId] : [createAssignmentDraft()]
          }));
        }
      } catch (error) {
        console.error('Teknik servis ekranı alınamadı:', error);
        setErrorMessage(getApiErrorMessage(error, 'Teknik servis talepleri y\u00fcklenemedi.'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [canViewRequests]);

  const requestRows = useMemo(
    () =>
      requests
        .map((request) => ({
          ...request,
          normalizedStatus: normalizeStatus(request.status),
          categoryLabel: getCategoryLabel(request),
          machineLabel: getMachineLabel(request),
          workSiteLabel: getWorkSiteLabel(request),
          statusMeta: getStatusMeta(request.status)
        }))
        .filter((request) => request.normalizedStatus === 1),
    [requests]
  );

  const selectedRequest = requestRows.find((request) => request.id === selectedRequestId) || requestRows[0] || null;
  const selectedDrafts = selectedRequest ? (selectedAssignments[selectedRequest.id] || [createAssignmentDraft()]) : [];

  const handleSelectRequest = (requestId) => {
    setSelectedRequestId(requestId);
    setSelectedAssignments((prev) => ({
      ...prev,
      [requestId]: prev[requestId]?.length ? prev[requestId] : [createAssignmentDraft()]
    }));
    setErrorMessage('');
    setSuccessMessage('');
  };

  const updateAssignmentDraft = (requestId, localId, userId) => {
    setSelectedAssignments((prev) => ({
      ...prev,
      [requestId]: (prev[requestId] || []).map((draft) =>
        draft.localId === localId ? { ...draft, userId } : draft
      )
    }));
  };

  const addAssignmentDraft = (requestId) => {
    setSelectedAssignments((prev) => ({
      ...prev,
      [requestId]: [...(prev[requestId] || []), createAssignmentDraft()]
    }));
  };

  const removeAssignmentDraft = (requestId, localId) => {
    setSelectedAssignments((prev) => {
      const nextDrafts = (prev[requestId] || []).filter((draft) => draft.localId !== localId);
      return {
        ...prev,
        [requestId]: nextDrafts.length > 0 ? nextDrafts : [createAssignmentDraft()]
      };
    });
  };

  const handleAssign = async (requestId) => {
    const assignedUserIds = Array.from(
      new Set(
        (selectedAssignments[requestId] || [])
          .map((draft) => draft.userId)
          .filter(Boolean)
      )
    );

    if (assignedUserIds.length === 0) {
      setErrorMessage('Atama yapmadan \u00f6nce en az bir teknik servis personeli se\u00e7in.');
      return;
    }

    setAssigningId(requestId);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await api.post('/TechnicalService/requests/assign', {
        requestId,
        assignedUserIds,
        note: assignmentNotes[requestId] || ''
      });

      setSuccessMessage('Personel atamas\u0131 yap\u0131ld\u0131. Talep art\u0131k servis durumu ekran\u0131na ta\u015f\u0131nd\u0131.');
      setRequests((prev) => {
        const nextItems = prev.filter((item) => item.id !== requestId);
        const nextAssignable = nextItems.filter((item) => normalizeStatus(item.status) === 1);
        const localNextRequestId = nextAssignable[0]?.id || null;
        setSelectedRequestId(localNextRequestId);
        if (localNextRequestId) {
          setSelectedAssignments((assignmentPrev) => ({
            ...assignmentPrev,
            [localNextRequestId]: assignmentPrev[localNextRequestId]?.length
              ? assignmentPrev[localNextRequestId]
              : [createAssignmentDraft()]
          }));
        }
        return nextItems;
      });
      setSelectedAssignments((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      setAssignmentNotes((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      const items = await fetchRequests();
      const assignable = items.filter((item) => normalizeStatus(item.status) === 1);
      const nextRequestId = assignable[0]?.id || null;
      setSelectedRequestId(nextRequestId);
      if (nextRequestId) {
        setSelectedAssignments((prev) => ({
          ...prev,
          [nextRequestId]: prev[nextRequestId]?.length ? prev[nextRequestId] : [createAssignmentDraft()]
        }));
      }
    } catch (error) {
      console.error('Atama yapılamadı:', error);
      setErrorMessage(getApiErrorMessage(error, 'Personel atamas\u0131 yap\u0131lamad\u0131.'));
    } finally {
      setAssigningId(null);
    }
  };

  const availablePersonnelOptions = (currentDraftId) => {
    const selectedUserIds = new Set(
      selectedDrafts
        .filter((draft) => draft.localId !== currentDraftId)
        .map((draft) => draft.userId)
        .filter(Boolean)
    );

    return personnelUsers.filter((user) => !selectedUserIds.has(user.id));
  };

  if (!canViewRequests) {
    return (
      <div className="technical-service-page">
        <Navbar />
        <div className="technical-service-container">
          <div className="technical-service-card">
            <h2>{'Bu alan yaln\u0131zca Teknik Servis M\u00fcd\u00fcr\u00fc i\u00e7in g\u00f6r\u00fcn\u00fcr.'}</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="technical-service-page">
      <Navbar />
      <div className="technical-service-container">
        {errorMessage && <div className="technical-service-alert error">{errorMessage}</div>}
        {successMessage && <div className="technical-service-alert success">{successMessage}</div>}

        <div className="technical-service-grid manager-layout assignment-layout">
          <section className="technical-service-card">
            <h2>{'A\u00e7\u0131lan Talepler'}</h2>
            {loading ? (
              <p>{'A\u00e7\u0131lan talepler y\u00fckleniyor...'}</p>
            ) : requestRows.length > 0 ? (
              <div className="request-stack refined-request-stack">
                {requestRows.map((request) => (
                  <button
                    key={request.id}
                    type="button"
                    className={`request-panel request-button refined-request-card ${selectedRequest?.id === request.id ? 'active' : ''}`}
                    onClick={() => handleSelectRequest(request.id)}
                  >
                    <div className="request-panel-header refined-request-header">
                      <div className="request-title-block">
                        <span className="request-code">{request.requestNo || `Talep #${request.id}`}</span>
                        <h3>{request.subject}</h3>
                        <p>{request.requestingCompanyName}</p>
                      </div>
                      <span className={`badge ${request.statusMeta.className}`}>
                        {request.statusMeta.label}
                      </span>
                      <div className="request-chip">
                        <span className="request-chip-label">Konum</span>
                        <strong>{request.workSiteLocation || '-'}</strong>
                      </div>
                      <div className="request-chip">
                        <span className="request-chip-label">Operasyon</span>
                        <strong>{formatDateTime(request.operationDate)}</strong>
                      </div>
                    </div>

                    <div className="request-detail-chips">
                      <div className="request-chip">
                        <span className="request-chip-label">Kategori</span>
                        <strong>{getCategoryLabel(request)}</strong>
                      </div>
                      <div className="request-chip">
                        <span className="request-chip-label">Makine</span>
                        <strong>{getMachineLabel(request)}</strong>
                      </div>
                      <div className="request-chip">
                        <span className="request-chip-label">{'\u015eantiye'}</span>
                        <strong>{getWorkSiteLabel(request)}</strong>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="empty-form-state compact assignment-empty-state">
                <h3>{'A\u00e7\u0131lan talep bulunmamaktad\u0131r'}</h3>
              </div>
            )}
          </section>

          <section className="technical-service-card">
            <h2>Personel Ata</h2>
            {selectedRequest ? (
              <div className="approval-panel">
                <div className="job-summary refined-summary">
                  <div><strong>Talep No:</strong> {selectedRequest.requestNo}</div>
                  <div><strong>Firma:</strong> {selectedRequest.requestingCompanyName}</div>
                  <div><strong>Makine:</strong> {selectedRequest.towerCraneSerialNo || selectedRequest.externalMachineName || '-'}</div>
                  <div><strong>{'\u015eantiye:'}</strong> {selectedRequest.workSiteName || selectedRequest.externalWorkSiteName || '-'}</div>
                </div>

                <div className="assign-box no-border">
                  <div className="assign-box-header">
                    <div>
                      <h4>{'Personel Se\u00e7imi'}</h4>
                    </div>
                    <button
                      type="button"
                      className="secondary-btn add-personnel-btn"
                      style={{ width: '170px', minWidth: '170px', maxWidth: '170px' }}
                      onClick={() => addAssignmentDraft(selectedRequest.id)}
                    >
                      + Personel Ekle
                    </button>
                  </div>

                  <div className="assignment-draft-list">
                    {selectedDrafts.map((draft, index) => (
                      <div key={draft.localId} className="assignment-draft-row">
                        <label>
                          <span>Personel {index + 1}</span>
                          <select
                            value={draft.userId}
                            onChange={(event) => updateAssignmentDraft(selectedRequest.id, draft.localId, event.target.value)}
                          >
                            <option value="">{'Teknik servis personeli se\u00e7in'}</option>
                            {availablePersonnelOptions(draft.localId).map((user) => (
                              <option key={user.id} value={user.id}>
                                {`${user.name || ''} ${user.surname || ''}`.trim()}
                              </option>
                            ))}
                          </select>
                        </label>

                        <button
                          type="button"
                          className="secondary-btn danger"
                          onClick={() => removeAssignmentDraft(selectedRequest.id, draft.localId)}
                          disabled={selectedDrafts.length === 1}
                        >{'Kald\u0131r'}</button>
                      </div>
                    ))}
                  </div>

                  <textarea
                    className="assignment-note"
                    placeholder="Atama notu"
                    rows="3"
                    value={assignmentNotes[selectedRequest.id] || ''}
                    onChange={(event) =>
                      setAssignmentNotes((prev) => ({
                        ...prev,
                        [selectedRequest.id]: event.target.value
                      }))
                    }
                  />

                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => handleAssign(selectedRequest.id)}
                    disabled={assigningId === selectedRequest.id}
                  >
                    {assigningId === selectedRequest.id ? 'Atan\u0131yor...' : 'Personel Atamas\u0131n\u0131 Tamamla'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="empty-form-state compact">
                <h3>{'Talep se\u00e7imi bekleniyor'}</h3>
                <p>{'Soldaki yeni taleplerden birini se\u00e7. Bu ekranda yaln\u0131zca a\u00e7\u0131lan taleplere personel atamas\u0131 yap\u0131l\u0131r.'}</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TechnicalServiceRequestsPage;
